<script setup>
import { computed } from 'vue';
import {
  useSidebar,
  SIDEBAR_WIDTH_MOBILE,
} from 'dashboard/composables/useSidebar';
import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
} from 'reka-ui';

const props = defineProps({
  side: {
    type: String,
    default: 'left',
    validator: v => ['left', 'right'].includes(v),
  },
  variant: {
    type: String,
    default: 'sidebar',
    validator: v => ['sidebar', 'floating', 'inset'].includes(v),
  },
  collapsible: {
    type: String,
    default: 'offcanvas',
    validator: v => ['offcanvas', 'icon', 'none'].includes(v),
  },
});

const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

const classes = computed(() => {
  const base =
    'flex h-full w-full flex-col text-sidebar-foreground overflow-hidden border-r';

  if (props.variant === 'floating' || props.variant === 'inset') {
    return `${base} rounded-lg border border-sidebar-border`;
  }

  return base;
});
</script>

<template>
  <!-- Mobile: Sheet/Dialog -->
  <DialogRoot v-if="isMobile" v-model:open="openMobile">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-40 bg-black/80" />
      <DialogContent
        data-sidebar="sidebar"
        data-mobile="true"
        :class="[
          side === 'left'
            ? 'left-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left'
            : 'right-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        ]"
        :style="{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE }"
        class="bg-n-sidebar-bg fixed inset-y-0 z-50 w-[--sidebar-width] p-0 text-sidebar-foreground transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out"
      >
        <div
          :class="classes"
          class="bg-n-sidebar-bg border-r"
          style="border-right-color: var(--sidebar-border)"
        >
          <slot />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>

  <!-- Desktop -->
  <div
    v-else
    class="peer hidden md:block text-sidebar-foreground"
    :data-state="state"
    :data-collapsible="state === 'collapsed' ? collapsible : ''"
    :data-variant="variant"
    :data-side="side"
  >
    <!-- Espace réservé au sidebar -->
    <div
      class="duration-200 relative h-svh w-[--sidebar-width] bg-transparent transition-[width] ease-linear"
      :class="[
        collapsible === 'offcanvas' && 'group-data-[collapsible=offcanvas]:w-0',
        side === 'right' && 'rotate-180',
        variant === 'floating' || variant === 'inset'
          ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
          : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]',
      ]"
    />
    <!-- Sidebar fixé -->
    <div
      class="duration-200 fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex"
      :class="[
        side === 'left'
          ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
          : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
        variant === 'floating' || variant === 'inset'
          ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
          : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l',
      ]"
    >
      <div
        data-sidebar="sidebar"
        :class="classes"
        class="bg-n-sidebar-bg border-r"
        style="border-right-color: var(--sidebar-border)"
      >
        <slot />
      </div>
    </div>
  </div>
</template>
